import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CookieManagementService } from '../services/cookie-management/cookie-management.service';
import { WebLogger } from "@weps/shared-http";

@Injectable()
export class CookieFilterInterceptor implements HttpInterceptor {

  private readonly COOKIE_SIZE_THRESHOLD = 3000; // 3000B threshold - conservative to avoid 413 errors

  constructor(private cookieManagementService: CookieManagementService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Check if this request should have cookie filtering applied
    if (!this.cookieManagementService.shouldFilterCookies(req.url, req.method)) {
      return next.handle(req);
    }

    WebLogger.log({
      message: 'Cookie filter interceptor triggered',
      level: 'debug',
      payload: {
        event_type: 'cookie_filter_triggered',
        http_method: req.method,
        http_url: this.sanitizeUrl(req.url),
        service: 'pwi'
      }
    });

    // Log current cookie stats before filtering
    this.cookieManagementService.logCookieStats();

    // Store current cookies for potential restoration
    const originalCookies = this.parseAndStoreCookies();
    let cookiesCleared = false;

    // Check if we need to reduce cookie size
    const currentSize = this.cookieManagementService.getCurrentCookieSize();

    if (currentSize > this.COOKIE_SIZE_THRESHOLD) {
      WebLogger.log({
        message: 'Cookie size exceeds threshold, clearing non-essential cookies',
        level: 'warn',
        payload: {
          event_type: 'cookie_cleanup_triggered',
          service: 'pwi',
          current_cookie_size: currentSize,
          threshold: this.COOKIE_SIZE_THRESHOLD
        }
      });
      this.cookieManagementService.clearNonEssentialCookies();
      cookiesCleared = true;

      // Log stats after cleanup
      this.cookieManagementService.logCookieStats();
    } else {
      WebLogger.log({
        message: 'Cookie size acceptable, no filtering needed',
        level: 'debug',
        payload: {
          event_type: 'cookie_size_check',
          service: 'pwi',
          current_cookie_size: currentSize,
          threshold: this.COOKIE_SIZE_THRESHOLD
        }
      });
    }

    // Continue with the request
    return next.handle(req).pipe(
      finalize(() => {
        if (cookiesCleared) {
          WebLogger.log({
            message: 'Request completed, restoring original cookies',
            level: 'debug',
            payload: {
              event_type: 'cookie_restoration',
              service: 'pwi'
            }
          });
          this.restoreOriginalCookies(originalCookies);
        }
      })
    );
  }

  private parseAndStoreCookies(): Map<string, string> {
    const cookieMap = new Map<string, string>();
    const cookies = document.cookie.split(';');

    cookies.forEach(cookie => {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie) {
        const [name, ...valueParts] = trimmedCookie.split('=');
        const value = valueParts.join('='); // Handle cookies with = in their value
        if (name) {
          cookieMap.set(name, value || '');
        }
      }
    });

    return cookieMap;
  }

  private restoreOriginalCookies(originalCookies: Map<string, string>): void {
    // Get current cookies to see what's missing
    const currentCookies = this.parseAndStoreCookies();

    // Restore any cookies that were removed
    originalCookies.forEach((value, name) => {
      if (!currentCookies.has(name)) {
        // Cookie was removed, restore it
        document.cookie = `${name}=${value}; path=/; domain=.vanguard.com; SameSite=Lax`;
      }
    });

    WebLogger.log({
      message: 'Cookie restoration complete',
      level: 'debug',
      payload: {
        event_type: 'cookie_restoration_complete',
        service: 'pwi',
        cookies_restored: originalCookies.size - currentCookies.size
      }
    });
  }

  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
      return url.split('?')[0].split('#')[0];
    }
  }
}
